import React from 'react';
import { get, has, isEmpty } from 'lodash';
import { constants } from '@clubmed/utils';
import { COLORS, COMPLEMENTARY_COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { getRentalAgesLabel } from 'Components/utils/product/product.utils';
import type { ProductFeatureCardProps } from 'Components/widgets/product-feature-card/ProductFeatureCard';
import 'Components/widgets/product-feature-card/ProductFeatureCard';
import type { ProductFeatureDetailProps } from 'Components/widgets/product-feature-detail/ProductFeatureDetail';
import 'Components/widgets/product-feature-detail/ProductFeatureDetail';
import type { ProductFeaturesCategoryProps } from 'Components/widgets/product-features/ProductFeatures';
import 'Components/widgets/product-features/ProductFeatures';
import { t } from 'Src/i18n';
import { formatApiDate } from 'Utils/date/apiDate';
import { DurationSlotsLabels } from 'Utils/childcare/childcare.utils';
import { featuresIcons } from './productFeatures.icons';

type CmsProductChildcaresLineProps = ProductChildcaresLineProps & {
  excursionPackingLabel?: string;
  excursionTransportationLabel?: string;
};

type ChildcareDetail = NonNullable<ProductFeatureDetailData['childcare']> & {
  timeSlots?: Array<{ opening: string; closing: string; type: string }>;
};

function getRank(item: ProductFeaturesCategoryData | ProductFeaturesActivityData): number {
  return Object.values(constants.RANKINGS).findIndex((ranking) => ranking === item.ranking);
}

function sortActivities(a, b) {
  return getRank(b) - getRank(a);
}

function getHowToBookDesc({ hasBookingOnline, hasBookingOnsite, config }) {
  if (!hasBookingOnline && !hasBookingOnsite) {
    return '';
  }

  if (hasBookingOnline && hasBookingOnsite) {
    return config.common.bookOnlineOrOnsite;
  }

  return hasBookingOnline ? config.common.bookOnlineOnly : config.common.bookOnsiteOnly;
}

function mapRentalEquipments(rentalEquipments, { age, size }: ProductChildcaresLineProps) {
  return rentalEquipments.map(
    ({ ageInMonths, id, label, longDescription, practiceLevels, sizes }) => {
      const items = [
        {
          description: longDescription,
        },
        {
          label: age,
          description: getRentalAgesLabel(ageInMonths),
        },
        {
          label: size,
          description: sizes.min
            ? `${sizes.min}${sizes.max ? ' - ' + sizes.max : ''} ${sizes.unit}`
            : '',
        },
      ].filter((item) => item.description);
      const level = practiceLevels[0]?.label;
      return {
        id,
        items,
        ...(level
          ? {
              level,
            }
          : {}),
        title: label,
      };
    },
  );
}

function mapServices(
  services = [],
  {
    included,
    onDemand,
    age,
    group,
    equipmentsLabel,
    utilitiesLabel,
    durationLabel,
    config,
    excursionPackingLabel = '',
    excursionTransportationLabel = '',
  }: CmsProductChildcaresLineProps,
) {
  return services.map(
    ({
      hasBookingOnline,
      hasBookingOnsite,
      id,
      isIdealForFamily,
      description,
      detailedDescription,
      ageRestriction,
      durationDescription,
      label,
      level,
      note,
      group: groupDescription,
      specificConditions,
      equipments,
      utilities,
      openingDates,
      transportation,
      whatToBring,
      targetPrice,
    }) => {
      const dateFormat = t('productActivitiesInformation.dateFormat');
      const items = [
        {
          label: t('productActivitiesInformation.openingDatesLabel'),
          items: (openingDates || []).map((days) => ({
            description: t('productActivitiesInformation.openingDates', {
              opening: formatApiDate(days.opening, dateFormat),
              closing: formatApiDate(days.closing, dateFormat),
            }),
          })),
        },
        {
          description,
        },
        {
          label: config.common.treatmentsLabel,
          description: detailedDescription,
        },
        {
          label: group,
          description: groupDescription,
        },
        {
          label: age,
          description: ageRestriction,
          ...(specificConditions
            ? {
                note: specificConditions,
              }
            : {}),
        },
        {
          label: excursionTransportationLabel,
          description: transportation,
        },
        {
          label: excursionPackingLabel,
          description: whatToBring,
        },
        {
          label: equipmentsLabel,
          items: (equipments || []).map((equipment) => ({
            description: equipment.label,
            tag: equipment.included
              ? {
                  color: COLORS.GREEN,
                  label: included,
                }
              : {
                  color: COLORS.ORANGE,
                  label: onDemand,
                },
          })),
        },
        {
          label: utilitiesLabel,
          items: (utilities || []).map((utility) => ({
            description: utility.label,
            tag: utility.included
              ? {
                  color: COLORS.GREEN,
                  label: included,
                }
              : {
                  color: COLORS.ORANGE,
                  label: onDemand,
                },
          })),
        },
        {
          label: config.common.howToBookLabel,
          description: getHowToBookDesc({
            hasBookingOnline,
            hasBookingOnsite,
            config,
          }),
        },
      ].filter((item) => item.description || (item.items && !isEmpty(item.items)));
      const price =
        targetPrice && targetPrice.price
          ? { ...targetPrice, label: get(config, 'common.price.priceFrom', '+{price}{currency}') }
          : null;
      const badges = isIdealForFamily
        ? [
            {
              label: config.common.idealForFamilyLabel,
              iconName: ICONS.IDEAL_FOR_FAMILY,
            },
          ]
        : null;
      return {
        ...(badges
          ? {
              badges,
            }
          : {}),
        id,
        items,
        ...(level
          ? {
              level,
            }
          : {}),
        ...(durationDescription
          ? {
              duration: {
                label: durationLabel,
                description: durationDescription,
              },
            }
          : {}),
        title: label,
        ...(note
          ? {
              note,
            }
          : {}),
        ...(price
          ? {
              targetPrice: price,
            }
          : {}),
      };
    },
  );
}

function mapFacilities(facilities = [], props: ProductChildcaresLineProps, inside) {
  const {
    distance: distanceLabel,
    indoor: indoorLabel,
    nightLighting: nightLightingLabel,
    outdoor: outdoorLabel,
    typeDescription: typeDescriptionLabel,
    location: locationLabel,
  } = props;
  return facilities
    .filter(({ onSite }) => onSite === inside)
    .map(
      ({
        id,
        description,
        distance,
        label,
        nightLighting,
        outdoor,
        quantity,
        specificInformation = [],
        technicalSpecificities = {},
        localisation,
      }) => {
        const basicInformation = [
          {
            label: distanceLabel,
            description: distance.toString(),
          },
          {
            description: nightLighting ? nightLightingLabel : '',
          },
          {
            description: outdoor ? outdoorLabel : indoorLabel,
          },
          {
            label: locationLabel,
            description: localisation,
          },
        ];
        const mapSpecificInformation = specificInformation.map((information) => ({
          label: get(props, get(information, 'type'), ''),
          description: get(information, 'value', ''),
        }));
        const mapTechnicalSpecificities = Object.keys(technicalSpecificities)
          .filter(
            (key) =>
              key !== 'unit' &&
              technicalSpecificities[key] &&
              !isEmpty(technicalSpecificities[key].toString()),
          )
          .map((key) => {
            if (key === 'typeDescription') {
              return {
                label: typeDescriptionLabel,
                description: technicalSpecificities.typeDescription,
              };
            }

            return {
              label: get(props, key, ''),
              description: `${technicalSpecificities[key]} ${get(technicalSpecificities, 'unit', 'm')}`,
            };
          });
        const items = [
          ...basicInformation,
          ...mapTechnicalSpecificities,
          ...mapSpecificInformation,
        ].filter((item) => item && item.description);
        return {
          id,
          description,
          items,
          title: label,
          quantity,
        };
      },
    );
}

function mapGeneralInformation(activityId, props, activities: Array<ProductFeaturesActivityData>) {
  const activity = activities.find(({ id }) => id === activityId);

  if (!activity) {
    return [];
  }

  const { bookingLabel, termsAndConditionsLabel, supervisionLabel, equipmentRentalLabel } = props;
  const {
    bookingDescription,
    termsAndConditionsDescription,
    supervisionDescription,
    equipmentRentalDescription,
  } = activity;
  return [
    {
      label: bookingLabel,
      description: bookingDescription || '',
    },
    {
      label: termsAndConditionsLabel,
      description: termsAndConditionsDescription || '',
    },
    {
      label: supervisionLabel,
      description: supervisionDescription || '',
    },
    {
      label: equipmentRentalLabel,
      description: equipmentRentalDescription || '',
    },
  ].filter((info) => info.description && info.description.trim());
}

export function mapChildcareInformation(
  { dateFormat, opening, openingDatesLabel, philosophy }: ProductChildcaresLineCMSProps,
  childcare: Partial<ChildcareDetail> = {},
) {
  if (isEmpty(childcare)) {
    return {};
  }

  const openingDates = (childcare.openingDates || [])
    .map(
      (days) =>
        `• ${t('productChildcaresInformation.openingDates', {
          opening: formatApiDate(days.opening, dateFormat),
          closing: formatApiDate(days.closing, dateFormat),
        })}`,
    )
    .join('\n');
  const items = [
    {
      label: openingDatesLabel,
      description: openingDates,
    },
    {
      label: philosophy,
      description: childcare.philosophy,
    },
  ].filter((item) => has(item, 'description') || has(item, 'note'));
  const openingHoursLab = (
    <DurationSlotsLabels
      timeSlots={childcare?.timeSlots || []}
      closingWeekdays={childcare?.closingWeekdays || []}
      classNames="font-sans text-14"
    />
  );
  return {
    duration: {
      label: opening,
      description: openingHoursLab,
      disclaimer: t('booking.services.childcare.openingDisclaimer'),
    },
    items,
    note: childcare.furtherInformation,
  };
}

function mapActivityData({
  activities,
  activityDetail,
  activityId = '',
  bookletUrl,
  brandLogoUrl,
  color,
  description,
  icon = ICONS.TRIDENT1,
  image,
  isIncluded,
  notIncludedMessage,
  philosophy,
  productFeaturesLineData,
  title,
  titleColor,
}: {
  activities: Array<ProductFeaturesActivityData>;
  activityDetail: Partial<ProductFeatureDetailData>;
  activityId: string;
  bookletUrl: string;
  brandLogoUrl: string | null | undefined;
  color: string | null | undefined;
  description: string | null | undefined;
  hasBanner: boolean;
  icon: string | null | undefined;
  image: string;
  isIncluded: boolean;
  notIncludedMessage?: string;
  philosophy: string | null | undefined;
  productFeaturesLineData: ProductChildcaresLineProps;
  title: string;
  titleColor: string | null | undefined;
}): ProductFeatureDetailProps {
  const {
    childcare = {},
    facilities = [],
    includedServices = [],
    onDemandServices = [],
    rentalEquipments = [],
    resort = { facilities: [], productActivities: [] },
  } = activityDetail;
  const isFacilityActivity = ['services_and_facilities_0_2', 'services_and_facilities_2_3'].some(
    (id) => activityId.includes(id),
  );
  const {
    config,
    facilitiesTitle,
    generalInformationTitle,
    includedTitle,
    onDemandEquipments,
    onDemandTitle,
    outsideFacilitiesTitle,
    resortFacilitiesTitle,
  } = productFeaturesLineData;
  const COLOR = color || COLORS.SAFFRON;
  return {
    ...(description
      ? {
          description,
        }
      : null),
    diamond: {
      className: `border-${COLOR} bg-${COLOR} text-${COMPLEMENTARY_COLORS[COLOR]}`,
      iconName: icon,
    },
    downloadLinksList: [
      {
        iconName: ICONS.DOWNLOAD,
        label: config.common.downloadLeafletLabel,
        url: bookletUrl,
      },
    ].filter((i) => i.url),
    equipmentsTitle: get(config, 'common.allInclusive', ''),
    equipments: isFacilityActivity
      ? [...(resort.facilities || []), ...(resort.productActivities || [])].map((e) => ({
          items: e.equipments.map((i) => ({
            iconName: ICONS[i.id],
            label: i.label,
          })),
          title: e.subtitle || e.label,
        }))
      : null,
    immersive: {
      url: image,
    },
    brandInformation: isEmpty(childcare)
      ? {
          description: philosophy,
          imageSrc: brandLogoUrl,
          title: config.common.philosophyLabel,
        }
      : null,
    mainInformationTitle: get(childcare, 'label', ''),
    mainInformation: mapChildcareInformation(
      productFeaturesLineData,
      childcare,
    ) as ProductFeatureCardProps['mainInformation'],
    isIncluded,
    notIncludedMessage: notIncludedMessage || '',
    includedMessage: '',
    includedServices: mapServices(includedServices, productFeaturesLineData),
    includedTitle,
    onDemandEquipmentsTitle: onDemandEquipments,
    onDemandServices: mapServices(onDemandServices, productFeaturesLineData),
    onDemandTitle,
    insideFacilities: mapFacilities(facilities, productFeaturesLineData, true),
    outsideFacilities: mapFacilities(facilities, productFeaturesLineData, false),
    facilitiesTitle,
    insideFacilitiesTitle: resortFacilitiesTitle,
    outsideFacilitiesTitle,
    targetPrice: {
      currency: get(childcare, 'targetPrice.currency', ''),
      price: get(childcare, 'targetPrice.price', 0),
      label: productFeaturesLineData?.config.common.price?.priceFrom || '',
    },
    generalInformation: mapGeneralInformation(activityId, productFeaturesLineData, activities),
    generalInformationTitle,
    generalInformationExtraCost: productFeaturesLineData.config.common.extraCost,
    rentalEquipments: mapRentalEquipments(rentalEquipments, productFeaturesLineData),
    title,
    titleColor,
  };
}

function resolveActivityServices({
  activityId,
  color,
  description,
  hasBanner,
  icon,
  image,
  isIncluded,
  title,
  titleColor,
  notIncludedMessage,
  productFeaturesLineData,
  activities,
  philosophy,
  brandLogoUrl,
  bookletUrl,
}: {
  activityId: string;
  color: string | null | undefined;
  description: string | null | undefined;
  hasBanner: boolean;
  icon: string | null | undefined;
  image: string;
  isIncluded: boolean;
  title: string;
  titleColor: string | null | undefined;
  notIncludedMessage?: string;
  productFeaturesLineData: ProductChildcaresLineProps;
  activities: Array<ProductFeaturesActivityData>;
  philosophy: string | null | undefined;
  brandLogoUrl: string | null | undefined;
  bookletUrl: string;
}) {
  return ({ productActivities }: { productActivities: ProductFeatureDetailData }) =>
    mapActivityData({
      activityId,
      color,
      description,
      hasBanner,
      icon,
      image,
      isIncluded,
      title,
      titleColor,
      activityDetail: productActivities,
      notIncludedMessage,
      productFeaturesLineData,
      activities,
      philosophy,
      brandLogoUrl,
      bookletUrl,
    });
}

function getActivityKeyword(keywords: ReadonlyArray<string> = []) {
  const keyword = keywords.find((k) => featuresIcons.hasOwnProperty(k));
  return keyword
    ? featuresIcons[keyword]
    : {
        icon: ICONS.TRIDENT1,
        color: undefined,
        textColor: undefined,
      };
}

export function mapActivitiesPerCategory({
  activities = [],
  categoryId = '',
  fetchActivityServices,
  hasBanner = false,
  notIncludedMessage,
  productFeaturesLineData,
  labelGAPrefix,
}: {
  activities: Array<ProductFeaturesActivityData>;
  categoryId: string;
  fetchActivityServices?: ProductFeatureFetchDetailFunction;
  hasBanner?: boolean;
  notIncludedMessage?: string;
  productFeaturesLineData: ProductChildcaresLineProps;
  labelGAPrefix: string;
}): Array<ProductFeatureCardProps> {
  const { config, includedTitle, onDemandTitle, resortId, seeMore } = productFeaturesLineData;
  return activities
    .sort(sortActivities)
    .map<ProductFeatureCardProps | null>(
      ({
        id: activityId,
        isChildrenClub,
        isIncluded,
        label,
        name,
        description,
        keywords,
        image,
        numberOfIncludedActivities,
        numberOfOnDemandActivities,
        philosophy,
        brandLogoUrl,
        bookletUrl,
        rentalEquipmentsUsecasesId,
      }) => {
        if (!image) {
          image = 'invalid';
        }

        if (!activityId || !name || !label) {
          return null;
        }

        const { icon, color, textColor } = getActivityKeyword(keywords || []);
        const hasOnDemandBanner = hasBanner && !isIncluded;
        const hasIncludedBanner = hasBanner && isChildrenClub && isIncluded;
        const tColor = textColor || color ? textColor || color : COLORS.BLACK;
        return {
          ...(hasOnDemandBanner || hasIncludedBanner
            ? {
                banner: {
                  color: isIncluded ? COLORS.GREEN : COLORS.ORANGE,
                  iconName: isIncluded ? ICONS.ALL_INCLUSIVE : ICONS.ON_DEMAND,
                  label: isIncluded ? config.common.included : config.common.childcarealacarte,
                },
              }
            : {}),
          counters: [
            {
              color: COLORS.GREEN,
              count: numberOfIncludedActivities || 0,
              icon: ICONS.ALL_INCLUSIVE,
              label: includedTitle,
            },
            {
              color: COLORS.ORANGE,
              count: numberOfOnDemandActivities || 0,
              icon: ICONS.ON_DEMAND,
              label: rentalEquipmentsUsecasesId ? t('common.onDemand') : onDemandTitle,
            },
          ],
          fetchDetail: () =>
            fetchActivityServices &&
            fetchActivityServices({
              activityId,
              productId: resortId,
              rentalEquipmentsUsecasesId,
            }).then(
              resolveActivityServices({
                activityId,
                color,
                description,
                hasBanner,
                icon,
                image,
                isIncluded,
                notIncludedMessage,
                philosophy,
                brandLogoUrl,
                title: label,
                titleColor: tColor,
                productFeaturesLineData,
                activities,
                bookletUrl,
              }),
            ),
          imageSrc: image,
          trackingData: {
            actionGA: 'blockInfos',
            labelGA: `${labelGAPrefix}_${categoryId}_${name}`,
          },
          seeMoreLabel: seeMore,
          title: label,
          titleColor: tColor,
        };
      },
    )
    .filter(Boolean);
}
export function mapCategories({
  fetchActivityServices,
  hasBanner,
  labelGAPrefix,
  notIncludedMessage,
  productActivitiesCategories,
  productFeaturesLineData,
}: {
  fetchActivityServices?: ProductFeatureFetchDetailFunction;
  hasBanner?: boolean;
  labelGAPrefix: string;
  notIncludedMessage?: string;
  productActivitiesCategories: Array<ProductFeaturesCategoryData>;
  productFeaturesLineData: ProductChildcaresLineProps;
}) {
  return productActivitiesCategories
    .sort(sortActivities)
    .map<ProductFeaturesCategoryProps | null>((category) => {
      const { id, activities, label } = category ?? {};
      if (!id || !label) {
        return null;
      }

      const activitiesItems = mapActivitiesPerCategory({
        activities,
        categoryId: id,
        fetchActivityServices,
        hasBanner,
        labelGAPrefix,
        notIncludedMessage,
        productFeaturesLineData,
      });
      return {
        activities: activitiesItems,
        backNavigationLabel: productFeaturesLineData.config.common.backNavigation,
        errorMessage: productFeaturesLineData.noDetailsErrorMessage,
        id,
        title: label,
      };
    })
    .filter(Boolean);
}
